import SweetAlert from "../../../components/sweetalert/SweetAlert";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import Swal from "sweetalert2";
import { downloadBase64File } from "../../../utils/fileDownloads";
import { getCookie } from "../../../store/localStorage";
import * as types from "./adminActionType";
import * as adminServices from "./adminServices";
import * as actions from "./adminActions";

//------------------Region - Start------------------//
// Worker saga: will be fired on FETCH_REGIONS_REQUEST actions
function* fetchRegionsSaga() {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(adminServices.getAllRegions);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchRegionsSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch regions";

    console.error("Region API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchRegionsFailure(errorMessage));
  }
}

// Worker saga for delete region
function* deleteRegionSaga(action) {
  try {
    const { regionId, modifiedBy } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.deleteRegion,
      regionId,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.deleteRegionSuccess(regionId));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Region deleted successfully.",
      });
      yield put(actions.fetchRegionsRequest());
    } else {
      yield put(
        actions.deleteRegionFailure(
          response?.errorMessage || "Failed to delete region",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to delete region.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete region";
    yield put(actions.deleteRegionFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for save region
function* saveRegionSaga(action) {
  try {
    // Call the API
    const response = yield call(adminServices.saveRegion, action.payload);
    if (response?.success) {
      yield put(actions.saveRegionSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Region saved successfully.",
      });
      yield put(actions.fetchRegionsRequest());
      if (typeof action.onSuccess === "function") {
        action.onSuccess(response);
      }
    } else {
      yield put(
        actions.saveRegionFailure(
          response?.errorMessage || "Failed to save region",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to save region.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save region";
    yield put(actions.saveRegionFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for fetch region by ID
function* fetchRegionByIdSaga(action) {
  try {
    const { regionId } = action.payload;

    // Call the API
    const response = yield call(adminServices.getRegionById, regionId);

    // Dispatch success action
    yield put(actions.fetchRegionByIdSuccess(response.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch region details";
    yield put(actions.fetchRegionByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for update region
function* updateRegionSaga(action) {
  try {
    const regionData = action.payload;
    const userData = yield call(getCookie, "userData");
    const userId = userData?.UserId || userData?.UserID || 10; // Get actual user ID from cookies or fallback

    // Prepare the payload as per API requirements
    const payload = {
      regionID: regionData.regionID || regionData.regionId,
      regionName: regionData.regionName,
      regionCode: regionData.regionCode || "",
      isActive: regionData.isActive,
      ModifiedBy: userId,
    };

    // Call the API
    const response = yield call(adminServices.updateRegion, payload);

    // Dispatch success action
    yield put(actions.updateRegionSuccess(response));
    SweetAlert.success({
      title: "Success!",
      text: response?.resultMessage || "Region updated successfully.",
    });
    yield put(actions.fetchRegionsRequest());
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update region";
    yield put(actions.updateRegionFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for activate/deactivate region
function* activateDeactivateRegionSaga(action) {
  try {
    const { regionId, status, userId } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.activateDeactivateRegion,
      regionId,
      status,
      userId,
    );
    yield put(actions.activateDeactivateRegionSuccess(response));
    if (response && response.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Region has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchRegionsRequest());
    } else {
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to update region status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update region status";
    yield put(actions.activateDeactivateRegionFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}
//------------------Region - End------------------//

//------------------National - Start------------------//
// Worker saga: will be fired on FETCH_NATIONALS_REQUEST actions
function* fetchNationalsSaga() {
  try {
    const response = yield call(adminServices.getAllNationals);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchNationalsSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch nationals";

    console.error("National API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    yield put(actions.fetchNationalsFailure(errorMessage));
  }
}

// Worker saga for delete national
function* deleteNationalSaga(action) {
  try {
    const { nationalId, modifiedBy } = action.payload;
    const response = yield call(
      adminServices.deleteNational,
      nationalId,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.deleteNationalSuccess(nationalId));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "National deleted successfully.",
      });
      yield put(actions.fetchNationalsRequest());
    } else {
      yield put(
        actions.deleteNationalFailure(
          response?.errorMessage || "Failed to delete national",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to delete national.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete national";
    yield put(actions.deleteNationalFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for save national
function* saveNationalSaga(action) {
  try {
    const response = yield call(adminServices.saveNational, action.payload);
    if (response?.success) {
      yield put(actions.saveNationalSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "National saved successfully.",
      });
      yield put(actions.fetchNationalsRequest());
    } else {
      yield put(
        actions.saveNationalFailure(
          response?.errorMessage || "Failed to save national",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to save national.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save national";
    yield put(actions.saveNationalFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for fetch national by ID
function* fetchNationalByIdSaga(action) {
  try {
    const { nationalId } = action.payload;

    const response = yield call(adminServices.getNationalById, nationalId);

    yield put(actions.fetchNationalByIdSuccess(response.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch national details";
    yield put(actions.fetchNationalByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for activate/deactivate national
function* activateDeactivateNationalSaga(action) {
  try {
    const { nationalId, status, userId } = action.payload;
    const response = yield call(
      adminServices.activateDeactivateNational,
      nationalId,
      status,
      userId,
    );
    yield put(actions.activateDeactivateNationalSuccess(response));
    if (response && response.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `National has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchNationalsRequest());
    } else {
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to update national status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update national status";
    yield put(actions.activateDeactivateNationalFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}
//------------------National - End------------------//

//------------------Worklist------------------//
// Worker saga: will be fired on FETCH_WORKLIST_REQUEST actions
function* fetchWorklistSaga() {
  try {
    const response = yield call(adminServices.getworklist);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchWorkListSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch worklist";
    // Dispatch failure action with error
    yield put(actions.fetchWorkListFailure(errorMessage));
  }
}

// delete worklist saga
function* deleteWorklistSaga(action) {
  try {
    const { WorkListID, modifiedBy } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.deleteWorklist,
      WorkListID,
      modifiedBy,
    );
    if (response?.success) {
      yield put(actions.deleteWorkListSuccess(WorkListID));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Worklist deleted successfully.",
      });
      yield put(actions.fetchWorkListRequest());
    } else {
      yield put(
        actions.deleteWorkListFailure(
          response?.errorMessage || "Failed to delete worklist",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to delete worklist.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete worklist";
    yield put(actions.deleteWorkListFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// dropdown worklist saga
function* dropdownWorklistSaga() {
  try {
    const response = yield call(adminServices.getWorklistDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data
    yield put(actions.dropdownWorklistSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch worklist dropdown";
    // Dispatch failure action with error
    yield put(actions.dropdownWorklistFailure(errorMessage));
  }
}

// save worklist saga
function* saveWorklistSaga(action) {
  try {
    // Call the API
    const response = yield call(adminServices.saveWorklist, action.payload);
    if (response?.success) {
      yield put(actions.saveWorklistSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Worklist saved successfully.",
      });
      yield put(actions.fetchWorkListRequest());
    } else {
      yield put(
        actions.saveWorklistFailure(
          response?.errorMessage || "Failed to save worklist",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to save worklist.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save worklist";
    yield put(actions.saveWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for fetch worklist by ID
function* fetchWorklistByIdSaga(action) {
  try {
    const { worklistId } = action.payload;
    // Call the API
    const response = yield call(adminServices.getWorklistById, worklistId);
    // Dispatch success action
    yield put(actions.fetchWorklistByIdSuccess(response.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch worklist details";
    yield put(actions.fetchWorklistByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for activate/deactivate worklist
function* activateDeactivateWorklistSaga(action) {
  try {
    const { worklistId, status, userId } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.activateDeactivateWorklist,
      worklistId,
      status,
      userId,
    );
    // Dispatch success action
    yield put(actions.activateDeactivateWorklistSuccess(response));
    if (response && response.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Worklist has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchWorkListRequest());
    } else {
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to update worklist status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update worklist status";
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
    yield put(actions.activateDeactivateWorklistFailure(errorMessage));
  }
}

//------------------Worklist Definition------------------//
function* fetchWorklistDefinitionSaga(action) {
  try {
    const response = yield call(
      adminServices.getAllWorklistDefinition,
      action.payload,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchWorklistDefinitionSuccess(response?.values));
  } catch (error) {
    const errorMessage = "Failed to fetch worklist definition";
    yield put(actions.fetchWorklistDefinitionFailure(errorMessage));
  }
}

function* dropdownWorklistDefinitionTypeSaga() {
  try {
    const response = yield call(adminServices.getWorklistDefinitionTypeDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.dropdownWorklistDefinitionTypeSuccess(response?.values));
  } catch (error) {
    const errorMessage = "Failed to fetch worklist definition type dropdown";
    yield put(actions.dropdownWorklistDefinitionTypeFailure(errorMessage));
  }
}

function* dropdownWorklistDefinitionFacilitySaga() {
  try {
    const response = yield call(
      adminServices.getWorklistDefinitionFacilityDropdown,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.dropdownWorklistDefinitionFacilitySuccess(response?.values),
    );
  } catch (error) {
    const errorMessage = "Failed to fetch worklist definition facility dropdown";
    yield put(actions.dropdownWorklistDefinitionFacilityFailure(errorMessage));
  }
}

function* saveWorklistDefinitionSaga(action) {
  try {
    const response = yield call(
      adminServices.saveWorklistDefinition,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.saveWorklistDefinitionSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Successfully saved worklist definition.",
      });
      yield put(actions.fetchWorklistDefinitionRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save worklist definition.";
      yield put(actions.saveWorklistDefinitionFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save worklist definition";
    yield put(actions.saveWorklistDefinitionFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* deleteWorklistDefinitionSaga(action) {
  try {
    const { workListId, modifiedBy, filters } = action.payload;
    const response = yield call(
      adminServices.deleteWorklistDefinition,
      workListId,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.deleteWorklistDefinitionSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Successfully deleted worklist definition.",
      });
      yield put(actions.fetchWorklistDefinitionRequest(filters));
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete worklist definition.";
      yield put(actions.deleteWorklistDefinitionFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete worklist definition";
    yield put(actions.deleteWorklistDefinitionFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* activateDeactivateWorklistDefinitionSaga(action) {
  try {
    const { workListId, status, userId, filters } = action.payload;
    const response = yield call(
      adminServices.activateDeactivateWorklistDefinition,
      workListId,
      status,
      userId,
    );

    yield put(actions.activateDeactivateWorklistDefinitionSuccess(response));
    if (response?.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Worklist definition has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchWorklistDefinitionRequest(filters));
    } else {
      SweetAlert.error({
        title: "Error!",
        text:
          response?.errorMessage ||
          "Failed to update worklist definition status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update worklist definition status";
    yield put(actions.activateDeactivateWorklistDefinitionFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//------------------Worklist Group------------------//
function* fetchWorklistGroupSaga() {
  try {
    const response = yield call(adminServices.getAllWorklistGroup);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchWorklistGroupSuccess(response?.values));
  } catch (error) {
    const errorMessage = "Failed to fetch worklist group";
    yield put(actions.fetchWorklistGroupFailure(errorMessage));
  }
}

function* dropdownWorklistGroupWorklistSaga(action) {
  try {
    const response = yield call(
      adminServices.getWorklistGroupWorklistDropdown,
      action.payload,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.dropdownWorklistGroupWorklistSuccess(response?.values));
  } catch (error) {
    const errorMessage = "Failed to fetch worklist group worklist dropdown";
    yield put(actions.dropdownWorklistGroupWorklistFailure(errorMessage));
  }
}

function* saveWorklistGroupSaga(action) {
  try {
    const response = yield call(
      adminServices.saveWorklistGroup,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.saveWorklistGroupSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully saved worklist group.",
      });
      yield put(actions.fetchWorklistGroupRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save worklist group.";
      yield put(actions.saveWorklistGroupFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save worklist group";
    yield put(actions.saveWorklistGroupFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* deleteWorklistGroupSaga(action) {
  try {
    const { worklistGroupId, modifiedBy } = action.payload;
    const response = yield call(
      adminServices.deleteWorklistGroup,
      worklistGroupId,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.deleteWorklistGroupSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Successfully deleted worklist group.",
      });
      yield put(actions.fetchWorklistGroupRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete worklist group.";
      yield put(actions.deleteWorklistGroupFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete worklist group";
    yield put(actions.deleteWorklistGroupFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* activateDeactivateWorklistGroupSaga(action) {
  try {
    const { worklistGroupId, status, userId } = action.payload;
    const response = yield call(
      adminServices.activateDeactivateWorklistGroup,
      worklistGroupId,
      status,
      userId,
    );

    yield put(actions.activateDeactivateWorklistGroupSuccess(response));
    if (response?.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Worklist group has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchWorklistGroupRequest());
    } else {
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to update worklist group status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update worklist group status";
    yield put(actions.activateDeactivateWorklistGroupFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchTagCategoryConfigurationSaga(action) {
  try {
    const response = yield call(
      adminServices.getAllTagCategoryConfiguration,
      action.payload,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchTagCategoryConfigurationSuccess(response?.values));
  } catch (error) {
    yield put(
      actions.fetchTagCategoryConfigurationFailure(
        "Failed to fetch tag category configuration",
      ),
    );
  }
}

function* fetchTagCategoryConfigurationByIdSaga(action) {
  try {
    const { tagCategoryID } = action.payload;
    const response = yield call(
      adminServices.getTagCategoryConfigurationById,
      tagCategoryID,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchTagCategoryConfigurationByIdSuccess(response?.values),
    );
  } catch (error) {
    yield put(
      actions.fetchTagCategoryConfigurationByIdFailure(
        "Failed to fetch tag category configuration details by id",
      ),
    );
  }
}

function* saveTagCategoryConfigurationSaga(action) {
  try {
    const response = yield call(
      adminServices.saveTagCategoryConfiguration,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.saveTagCategoryConfigurationSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully saved tag category configuration.",
      });
      yield put(actions.fetchTagCategoryConfigurationRequest());
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to save tag category configuration.";
      yield put(actions.saveTagCategoryConfigurationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save tag category configuration";
    yield put(actions.saveTagCategoryConfigurationFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* activateDeactivateTagCategoryConfigurationSaga(action) {
  try {
    const { tagCategoryID, status, userId } = action.payload;
    const response = yield call(
      adminServices.activateDeactivateTagCategoryConfiguration,
      tagCategoryID,
      status,
      userId,
    );

    yield put(actions.activateDeactivateTagCategoryConfigurationSuccess(response));
    if (response?.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Tag category configuration has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchTagCategoryConfigurationRequest());
    } else {
      SweetAlert.error({
        title: "Error!",
        text:
          response?.errorMessage ||
          "Failed to update tag category configuration status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update tag category configuration status";
    yield put(
      actions.activateDeactivateTagCategoryConfigurationFailure(errorMessage),
    );
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchManageTagsShellSaga(action) {
  try {
    const { tagCategoryID } = action.payload;
    const response = yield call(adminServices.getManageTagsShell, tagCategoryID);
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(actions.fetchManageTagsShellSuccess(response?.values));
  } catch (error) {
    yield put(
      actions.fetchManageTagsShellFailure("Failed to fetch tags for category"),
    );
  }
}

function* fetchTagAssignmentsSaga(action) {
  try {
    const { tagCategoryID, tagID, resourceTagID, loadNonce } = action.payload;
    const response = yield call(adminServices.getTagAssignments, {
      TagCategoryID: tagCategoryID,
      TagID: tagID,
      ResourceTagID: resourceTagID,
    });
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchTagAssignmentsSuccess(
        tagID,
        resourceTagID,
        response?.values,
        loadNonce,
      ),
    );
  } catch (error) {
    yield put(
      actions.fetchTagAssignmentsFailure(
        action.payload?.tagID,
        action.payload?.resourceTagID,
        "Failed to fetch assigned entities",
        action.payload?.loadNonce,
      ),
    );
  }
}

function* saveManageTagsBatchSaga(action) {
  try {
    const response = yield call(adminServices.saveManageTagsBatch, action.payload);

    if (response?.success) {
      yield put(actions.saveManageTagsBatchSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully saved tag assignments.",
      });
      yield put(actions.fetchTagCategoryConfigurationRequest());
    } else {
      const errorMessage = response?.errorMessage || "Failed to save tag assignments.";
      yield put(actions.saveManageTagsBatchFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage || error.message || "Failed to save tags";
    yield put(actions.saveManageTagsBatchFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* saveTagSaga(action) {
  try {
    const response = yield call(adminServices.saveTag, action.payload);

    if (response?.success) {
      yield put(actions.saveTagSuccess(response?.values));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully saved tag.",
      });
    } else {
      const errorMessage = response?.errorMessage || "Failed to save tag.";
      yield put(actions.saveTagFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage || error.message || "Failed to save tag";
    yield put(actions.saveTagFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* deleteTagSaga(action) {
  try {
    const response = yield call(adminServices.deleteTag, action.payload);

    if (response?.success) {
      yield put(actions.deleteTagSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully deleted tag.",
      });
    } else {
      const errorMessage = response?.errorMessage || "Failed to delete tag.";
      yield put(actions.deleteTagFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage || error.message || "Failed to delete tag";
    yield put(actions.deleteTagFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchWorklistDefinitionConfigurationSaga(action) {
  try {
    const response = yield call(
      adminServices.getWorklistDefinitionConfigurationList,
      action.payload,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchWorklistDefinitionConfigurationSuccess(response?.values),
    );
  } catch (error) {
    const errorMessage = "Failed to fetch worklist configuration";
    yield put(actions.fetchWorklistDefinitionConfigurationFailure(errorMessage));
  }
}

function* fetchWorklistDefinitionConfigurationDropdownsSaga(action) {
  try {
    const { workListDefinationID, workListDefinationSiteMappingId } =
      action.payload;
    const response = yield call(
      adminServices.getWorklistDefinitionConfigurationDropdowns,
      workListDefinationID,
      workListDefinationSiteMappingId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchWorklistDefinitionConfigurationDropdownsSuccess(
        response?.values,
      ),
    );
  } catch (error) {
    const errorMessage =
      "Failed to fetch worklist configuration dropdown values";
    yield put(
      actions.fetchWorklistDefinitionConfigurationDropdownsFailure(errorMessage),
    );
  }
}

function* fetchWorklistDefinitionConfigurationByIdSaga(action) {
  try {
    const { workListDefinationSiteMappingId } = action.payload;
    const response = yield call(
      adminServices.getWorklistDefinitionConfigurationById,
      workListDefinationSiteMappingId,
    );
    if (!response) {
      throw new Error("No response received from API");
    }

    yield put(
      actions.fetchWorklistDefinitionConfigurationByIdSuccess(response?.values),
    );
  } catch (error) {
    const errorMessage =
      "Failed to fetch worklist configuration details by id";
    yield put(actions.fetchWorklistDefinitionConfigurationByIdFailure(errorMessage));
  }
}

function* saveWorklistDefinitionConfigurationSaga(action) {
  try {
    const response = yield call(
      adminServices.saveWorklistDefinitionConfiguration,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.saveWorklistDefinitionConfigurationSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully saved worklist configuration.",
      });
      yield put(
        actions.fetchWorklistDefinitionConfigurationRequest({
          WorkListDefinationID: action.payload?.WorkListDefinationID,
        }),
      );
      yield put(
        actions.fetchWorklistDefinitionConfigurationDropdownsRequest(
          action.payload?.WorkListDefinationID,
          0,
        ),
      );
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save worklist configuration.";
      yield put(actions.saveWorklistDefinitionConfigurationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save worklist configuration";
    yield put(actions.saveWorklistDefinitionConfigurationFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* deleteWorklistDefinitionConfigurationSaga(action) {
  try {
    const { workListDefinationSiteMappingId, workListDefinationID } =
      action.payload;
    const response = yield call(
      adminServices.deleteWorklistDefinitionConfiguration,
      workListDefinationSiteMappingId,
    );

    if (response?.success) {
      yield put(actions.deleteWorklistDefinitionConfigurationSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully deleted worklist configuration.",
      });
      yield put(
        actions.fetchWorklistDefinitionConfigurationRequest({
          WorkListDefinationID: workListDefinationID,
        }),
      );
      yield put(
        actions.fetchWorklistDefinitionConfigurationDropdownsRequest(
          workListDefinationID,
          0,
        ),
      );
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete worklist configuration.";
      yield put(actions.deleteWorklistDefinitionConfigurationFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete worklist configuration";
    yield put(actions.deleteWorklistDefinitionConfigurationFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* activateDeactivateWorklistDefinitionConfigurationSaga(action) {
  try {
    const { workListDefinationSiteMappingId, status, userId, workListDefinationID } =
      action.payload;
    const response = yield call(
      adminServices.activateDeactivateWorklistDefinitionConfiguration,
      workListDefinationSiteMappingId,
      status,
      userId,
    );

    yield put(
      actions.activateDeactivateWorklistDefinitionConfigurationSuccess(response),
    );
    if (response?.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Worklist configuration has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(
        actions.fetchWorklistDefinitionConfigurationRequest({
          WorkListDefinationID: workListDefinationID,
        }),
      );
    } else {
      SweetAlert.error({
        title: "Error!",
        text:
          response?.errorMessage ||
          "Failed to update worklist configuration status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update worklist configuration status";
    yield put(
      actions.activateDeactivateWorklistDefinitionConfigurationFailure(
        errorMessage,
      ),
    );
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//-----------------dropdown Api worklist start ----------------//

//// All master worklist dropdowns
function* dropdownMasterWorklistSaga() {
  try {
    const response = yield call(adminServices.getAllMasterWorklistDropdowns);
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.dropdownAllMasterWorklistSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch master worklist dropdowns";

    console.error("Master Worklist Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.dropdownAllMasterWorklistFailure(errorMessage));
  }
}

// Get Regional Practice Worklist dropdown
function* dropdownRegionalPracticeWorklistSaga(action) {
  try {
    const regionalPracticeID = action?.payload?.regionalPracticeID;
    if (!regionalPracticeID) {
      yield put(actions.dropdownRegionalPracticeWorklistSuccess([]));
      return;
    }
    const response = yield call(
      adminServices.getRegionalPracticeWorklistDropdown,
      regionalPracticeID,
    );
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(
      actions.dropdownRegionalPracticeWorklistSuccess(response?.values),
    );
  } catch (error) {
    let errorMessage = "Failed to fetch regional practice worklist dropdown";

    console.error("Regional Practice Worklist Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.dropdownRegionalPracticeWorklistFailure(errorMessage));
  }
}

//Main Practice
function* dropdownMainPracticeSaga(action) {
  try {
    const regionalPracticeID = action?.payload?.regionalPracticeID;
    if (!regionalPracticeID) {
      yield put(actions.dropdownMainPracticeSuccess([]));
      return;
    }
    const response = yield call(
      adminServices.getMainPracticeDropdown,
      regionalPracticeID,
    );
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.dropdownMainPracticeSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch main practice dropdown";

    console.error("Main Practice Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.dropdownMainPracticeFailure(errorMessage));
  }
}

//-------------dropdowns in Worklist end----------------

//------------------RPCE - Start------------------//
// Worker saga: will be fired on FETCH_RPCE_REQUEST actions
function* fetchRPCESaga() {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(adminServices.getAllRPCE);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchRPCESuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch RPCE";

    console.error("RPCE API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchRPCEFailure(errorMessage));
  }
}

// Worker saga for delete RPCE
function* deleteRPCESaga(action) {
  try {
    const { RPCEID, modifiedBy } = action.payload;
    // Call the API
    const response = yield call(adminServices.deleteRPCE, RPCEID, modifiedBy);
    if (response?.success) {
      yield put(actions.deleteRPCESuccess(RPCEID));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Deleted RPCE.",
      });
      yield put(actions.fetchRPCERequest());
    } else {
      yield put(
        actions.deleteRPCEFailure(
          response?.errorMessage || "Failed to delete RPCE",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to delete RPCE.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete RPCE";
    yield put(actions.deleteRPCEFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for save RPCE
function* saveRPCESaga(action) {
  try {
    // Call the API
    const response = yield call(adminServices.saveRPCE, action.payload);
    if (response?.success) {
      yield put(actions.saveRPCESuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "RPCE saved successfully.",
      });
      yield put(actions.fetchRPCERequest());
    } else {
      yield put(
        actions.saveRPCEFailure(
          response?.errorMessage || "Failed to save RPCE",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to save RPCE.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save RPCE";
    yield put(actions.saveRPCEFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for fetch RPCE by ID
function* fetchRPCEByIdSaga(action) {
  try {
    const { RPCEID } = action.payload;
    // Call the API
    const response = yield call(adminServices.getRPCEById, RPCEID);
    // Dispatch success action
    yield put(actions.fetchRPCEByIdSuccess(response.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch RPCE details";
    yield put(actions.fetchRPCEByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for activate/deactivate RPCE
function* activateDeactivateRPCESaga(action) {
  try {
    const { RPCEID, status, userId } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.activateDeactivateRPCE,
      RPCEID,
      status,
      userId,
    );
    yield put(actions.activateDeactivateRPCESuccess(response));
    if (response && response.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `RPCE has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchRPCERequest());
    } else {
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to update RPCE status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update RPCE status";
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
    yield put(actions.activateDeactivateRPCEFailure(errorMessage));
  }
}

// Worker saga for fetch
function* fetchPracticeSaga() {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(adminServices.getAllPractice);
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data
    yield put(actions.fetchPracticeSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch practice";
    // Dispatch failure action with error
    yield put(actions.fetchPracticeFailure(errorMessage));
  }
}

function* fetchpracticeByIdSaga(action) {
  try {
    const { practiceId } = action.payload;
    // Call the API
    const response = yield call(adminServices.getpracticeById, practiceId);
    // Dispatch success action
    yield put(actions.fetchPracticeByIdSuccess(response.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch practice details";
    yield put(actions.fetchPracticeByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// save Practice saga
function* savePracticeSaga(action) {
  try {
    // Call the API
    const response = yield call(adminServices.savePractice, action.payload);
    if (response?.success) {
      yield put(actions.savePracticeSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Practice saved successfully.",
      });
      yield put(actions.fetchPracticeRequest());
      if (typeof action.onSuccess === "function") {
        action.onSuccess(response);
      }
    } else {
      yield put(
        actions.savePracticeFailure(
          response?.errorMessage || "Failed to save practice",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to save practice.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save practice";
    yield put(actions.savePracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//----------Delete Practice----------//
function* deletePracticeSaga(action) {
  try {
    const { practiceId, modifiedBy } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.deletePractice,
      practiceId,
      modifiedBy,
    );
    if (response?.success) {
      yield put(actions.deletePracticeSuccess(practiceId));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Practice deleted successfully.",
      });
      yield put(actions.fetchPracticeRequest());
    } else {
      yield put(
        actions.deletePracticeFailure(
          response?.errorMessage || "Failed to delete practice",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to delete practice.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete practice";
    yield put(actions.deletePracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* activateDeactivatePracticeSaga(action) {
  try {
    const { PracticeID, status, userId } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.activateDeactivatePractice,
      PracticeID,
      status,
      userId,
    );
    yield put(actions.activateDeactivatePracticeSuccess(response));
    if (response && response.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Practice has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchPracticeRequest());
    } else {
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to update practice status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update practice status";
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
    yield put(actions.activateDeactivatePracticeFailure(errorMessage));
  }
}

//-----Worklist dropdown for practice------//
function* worklistDropdownDD() {
  try {
    const response = yield call(adminServices.getAllWorklistDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data
    yield put(actions.dropdownWorkListDDSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch worklist dropdown";

    console.error("Worklist Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    // Dispatch failure action with error
    yield put(actions.dropdownWorkListDDFailure(errorMessage));
  }
}

//-------Region by worklist id----------//
function* fetchRegionByWorklistIdSaga(action) {
  try {
    const { id } = action.payload;

    // Call the API
    const response = yield call(adminServices.getRegionByWorklistID, id);

    // Dispatch success action
    yield put(actions.fetchRegionByWorkListIdSuccess(response.values));
  } catch (error) {
    let errorMessage = "Failed to fetch region By WorklistId details";

    console.error("Fetch region By WorklistId API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchRegionByWorkListIdFailure(errorMessage));

    // Show error notification
    Swal.fire({
      title: "Error",
      text: errorMessage,
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#d33",
    });
  }
}

function* fetchMasterRegionDropdownSaga() {
  try {
    const response = yield call(adminServices.getAllMasterRegionDropdown);

    yield put(actions.dropdownMasterRegionSuccess(response?.values?.dropdownItems || []));
  } catch (error) {
    let errorMessage = "Failed to fetch master region dropdown";

    console.error("Master Region Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    yield put(actions.dropdownMasterRegionFailure(errorMessage));
  }
}

function* fetchMasterNationalDropdownSaga() {
  try {
    const response = yield call(adminServices.getAllMasterNationalDropdown);

    if (response?.success) {
      yield put(actions.dropdownMasterNationalSuccess(response?.values?.dropdownItems || []));
    } else {
      const errorMessage = response?.errorMessage || "Failed to fetch master national dropdown";

      yield put(actions.dropdownMasterNationalFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    let errorMessage = "Failed to fetch master national dropdown";

    console.error("Master National Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    yield put(actions.dropdownMasterNationalFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//------------------SubPractice  - Start------------------//
// Worker saga: will be fired on FETCH_SUBPRACTICES_REQUEST actions
function* fetchSubpracticeSaga() {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(adminServices.getAllSubpractice);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchSubpracticeSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch subpractices";

    console.error("Subpractice API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchSubpracticeFailure(errorMessage));
  }
}

// Worker saga for delete subpractice
function* deleteSubpracticeSaga(action) {
  try {
    const { SubPracticeID, modifiedBy } = action.payload;

    // Call the API
    const response = yield call(
      adminServices.deleteSubpractice,
      SubPracticeID,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.deleteSubpracticeSuccess(SubPracticeID));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Deleted Subpractice",
      });
      yield put(actions.fetchSubpracticeRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete subpractice";
      yield put(actions.deleteSubpracticeFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete subpractice";
    yield put(actions.deleteSubpracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for save subpractice
function* saveSubpracticeSaga(action) {
  try {
    // Call the API
    const response = yield call(adminServices.saveSubpractice, action.payload);
    if (response?.success) {
      yield put(actions.saveSubpracticeSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Saved Subpractice",
      });
      yield put(actions.fetchSubpracticeRequest());
      if (typeof action.onSuccess === "function") {
        action.onSuccess(response);
      }
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save subpractice";
      yield put(actions.saveSubpracticeFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save subpractice";
    yield put(actions.saveSubpracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// dropdown for subpractice saga
function* dropdownSubpracticeSaga() {
  try {
    const response = yield call(adminServices.getSubpracticeDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data

    yield put(actions.dropdownSubpracticeSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch subpractice dropdown";

    console.error("Subpractice Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    // Dispatch failure action with error
    yield put(actions.dropdownSubpracticeFailure(errorMessage));
  }
}

// Worker saga for fetch subpractice by ID
function* fetchSubpracticeByIdSaga(action) {
  try {
    const { SubPracticeID } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.getSubpracticeById,
      SubPracticeID,
    );
    // Dispatch success action
    yield put(actions.fetchSubpracticeByIdSuccess(response.values));
  } catch (error) {
    let errorMessage = "Failed to fetch subpractice details";
    console.error("Fetch Subpractice By ID API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    // Dispatch failure action with error
    yield put(actions.fetchSubpracticeByIdFailure(errorMessage));
    // Show error notification
    Swal.fire({
      title: "Error",
      text: errorMessage,
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#d33",
    });
  }
}

// Worker saga for activate/deactivate subpractice
function* activateDeactivateSubpracticeSaga(action) {
  try {
    const { SubPracticeID, status, userId } = action.payload;

    // Call the API
    const response = yield call(
      adminServices.activateDeactivateSubpractice,
      SubPracticeID,
      status,
      userId,
    );

    if (response?.success) {
      yield put(actions.activateDeactivateSubpracticeSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Subpractice has been ${status ? "activated" : "deactivated"} successfully`,
      });
      yield put(actions.fetchSubpracticeRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to update subpractice status";
      yield put(actions.activateDeactivateSubpracticeFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update subpractice status";
    yield put(actions.activateDeactivateSubpracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}
//------------------SubPractice  - End------------------//

//------------------SectionWorklist Mapping - start------------------//
// Worker saga for getAll actions
function* fetchSectionWorklistSaga() {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(adminServices.getAllSectionWorklist);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchSectionWorklistSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch section worklists";

    console.error("Section Worklist API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchSectionWorklistFailure(errorMessage));
  }
}

// Worker saga for delete section worklist
function* deleteSectionWorklistSaga(action) {
  try {
    const { SectionID, modifiedBy } = action.payload;

    // Call the API
    const response = yield call(
      adminServices.deleteSectionWorklist,
      SectionID,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.deleteSectionWorklistSuccess(SectionID));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Successfully Deleted Section Worklist",
      });
      yield put(actions.fetchSectionWorklistRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete section worklist";
      yield put(actions.deleteSectionWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete section worklist";
    yield put(actions.deleteSectionWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for save section worklist
function* saveSectionWorklistSaga(action) {
  try {
    // Call the API
    const response = yield call(
      adminServices.saveSectionWorklist,
      action.payload,
    );
    if (response?.success) {
      yield put(actions.saveSectionWorklistSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Saved Section Worklist",
      });
      yield put(actions.fetchSectionWorklistRequest());
      if (typeof action.onSuccess === "function") {
        action.onSuccess(response);
      }
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save section worklist";
      yield put(actions.saveSectionWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save section worklist";
    yield put(actions.saveSectionWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// dropdown for section worklist saga
function* dropdownSectionWorklistSaga() {
  try {
    const response = yield call(adminServices.getSectionWorklistDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }
    // Dispatch success action with the data

    yield put(actions.dropdownSectionWorklistSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch section worklist dropdown";

    console.error("Section Worklist Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    // Dispatch failure action with error
    yield put(actions.dropdownSectionWorklistFailure(errorMessage));
  }
}

// Worker saga for fetch section worklist by ID
function* fetchSectionWorklistByIdSaga(action) {
  try {
    const { SectionID } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.getSectionWorklistById,
      SectionID,
    );
    // Dispatch success action
    yield put(actions.fetchSectionWorklistByIdSuccess(response.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch section worklist details";
    yield put(actions.fetchSectionWorklistByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for activate/deactivate section worklist
function* activateDeactivateSectionWorklistSaga(action) {
  try {
    const { SectionID, status, userId } = action.payload;

    // Call the API
    const response = yield call(
      adminServices.activateDeactivateSectionWorklist,
      SectionID,
      status,
      userId,
    );

    if (response?.success) {
      yield put(actions.activateDeactivateSectionWorklistSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Section Worklist has been ${status ? "activated" : "deactivated"} successfully`,
      });
      yield put(actions.fetchSectionWorklistRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to update section worklist status";
      yield put(actions.activateDeactivateSectionWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update section worklist status";
    yield put(actions.activateDeactivateSectionWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}
//------------------SectionWorklist Mapping - end------------------//

// //------------------User Management - start-------------------//
// // Worker saga: will be fired on FETCH_USERMANAGEMENT_REQUEST actions
// function* fetchUserManagementSaga() {
//     try {
//         // Call the API - request utility already returns response.data
//         const response = yield call(adminServices.getAllUserManagement);
//         if (!response) {
//             throw new Error('No response received from API');
//         }

//         // Dispatch success action with the data
//         yield put(actions.fetchUserManagementSuccess(response?.values));

//     } catch (error) {
//         let errorMessage = 'Failed to fetch User Management';

//         console.error('User Management API Error:', {
//             status: error.response?.status,
//             message: errorMessage,
//             data: error.response?.data,
//             timestamp: new Date().toISOString()
//         });

//         // Dispatch failure action with error
//         yield put(actions.fetchUserManagementFailure(errorMessage));
//     }
// }

// // Worker saga for delete user management
// function* deleteUserManagementSaga(action) {
//     try {
//         const { UserId, modifiedBy } = action.payload;

//         // Call the API
//         const response = yield call(adminServices.deleteUserManagement, UserId, modifiedBy);

//         if (response?.success) {
//             yield put(actions.deleteUserManagementSuccess(UserId));
//             SweetAlert.success({
//                 title: 'Success!',
//                 text: response?.resultMessage || 'Successfully Deleted User Management',
//                 showConfirmButton: true
//             });
//             yield put(actions.fetchUserManagementRequest());
//         } else {
//             const errorMessage = response?.errorMessage || 'Failed to delete user management';
//             yield put(actions.deleteUserManagementFailure(errorMessage));
//             SweetAlert.error({
//                 title: 'Error!',
//                 text: errorMessage,
//                 showConfirmButton: true
//             });
//         }
//     } catch (error) {
//         const errorMessage = error.response?.data?.errorMessage || error.message || 'Failed to delete user management';
//         yield put(actions.deleteUserManagementFailure(errorMessage));
//         SweetAlert.error({
//             title: 'Error!',
//             text: errorMessage,
//             showConfirmButton: true
//         });
//     }
// }

// // Worker saga for activate/deactivate user management
// function* activateDeactivateUserManagementSaga(action) {
//     try {
//         const { Id, userId, status } = action.payload || {};
//         //const targetUserId = Id || userId;

//         // Call the API
//         const response = yield call(adminServices.activateDeactivateUserManagement, Id, status, userId);

//         if (response?.success) {
//             yield put(actions.activateDeactivateUserManagementSuccess(response));
//             SweetAlert.success({
//                 title: 'Success!',
//                 text: response?.resultMessage || `User Management has been ${status ? 'activated' : 'deactivated'} successfully`,
//                 showConfirmButton: true
//             });
//             yield put(actions.fetchUserManagementRequest());
//         } else {
//             const errorMessage = response?.errorMessage || 'Failed to update user management status';
//             yield put(actions.activateDeactivateUserManagementFailure(errorMessage));
//             SweetAlert.error({
//                 title: 'Error!',
//                 text: errorMessage,
//                 showConfirmButton: true

//             });
//         }
//     } catch (error) {
//         const errorMessage = error.response?.data?.errorMessage || error.message || 'Failed to update user management status';
//         yield put(actions.activateDeactivateUserManagementFailure(errorMessage));
//         SweetAlert.error({
//             title: 'Error!',
//             text: errorMessage,
//             showConfirmButton: true
//         });
//     }
// }

// // Worker saga for save user management (add/update)
// function* saveUserManagementSaga(action) {
//     try {
//         // Call the API
//         const response = yield call(adminServices.saveUserManagement, action.payload);
//         if (response?.success) {
//             yield put(actions.saveUserManagementSuccess(response));
//             SweetAlert.success({
//                 title: 'Success!',
//                 text: response?.resultMessage || 'User successfully saved',
//                 showConfirmButton: true
//             });
//             yield put(actions.fetchUserManagementRequest());
//         } else {
//             const errorMessage = response?.errorMessage || 'Failed to save user';
//             yield put(actions.saveUserManagementFailure(errorMessage));
//             SweetAlert.error({
//                 title: 'Error!',
//                 text: errorMessage,
//                 showConfirmButton: true
//             });
//         }
//     } catch (error) {
//         const errorMessage = error.response?.data?.errorMessage || error.message || 'Failed to save user';
//         yield put(actions.saveUserManagementFailure(errorMessage));
//         SweetAlert.error({
//             title: 'Error!',
//             text: errorMessage,
//             showConfirmButton: true
//         });
//     }
// }

// // dropdown for USermanagement role saga
// function* dropdownUserManagementRoleSaga() {
//     try {
//         const response = yield call(adminServices.getUserManagementRoleDropdown);
//         if (!response) {
//             throw new Error('No response received from API');
//         }
//         // Dispatch success action with the data

//         yield put(actions.dropdownUserManagementRoleSuccess(response?.values));

//     } catch (error) {
//         let errorMessage = 'Failed to fetch user management dropdown';

//         console.error('User Management Dropdown API Error:', {
//             status: error.response?.status,
//             message: errorMessage,
//             data: error.response?.data,
//             timestamp: new Date().toISOString()
//         });
//         // Dispatch failure action with error
//         yield put(actions.dropdownUserManagementRoleFailure(errorMessage));
//     }

// }

// // dropdown for subpractice saga
// function* dropdownUserManagementDesignationSaga() {
//     try {
//         const response = yield call(adminServices.getUserManagementDesignationDropdown);
//         if (!response) {
//             throw new Error('No response received from API');
//         }
//         // Dispatch success action with the data

//         yield put(actions.dropdownUserManagementDesignationSuccess(response?.values));

//     } catch (error) {
//         let errorMessage = 'Failed to fetch User Management dropdown';
//         // Dispatch failure action with error
//         yield put(actions.dropdownUserManagementDesignationFailure(errorMessage));
//     }

// }

// // Worker saga for fetch subpractice by ID
// function* fetchUserManagementIdSaga(action) {
//     try {
//         const { userId } = action.payload;
//         // Call the API
//         const response = yield call(adminServices.getUSerManagementById, userId);
//         // Dispatch success action
//         yield put(actions.fetchUserManagementByIdSuccess(response.values));
//     } catch (error) {
//         const errorMessage = error.response?.data?.errorMessage || error.message || 'Failed to fetch user management details';
//         yield put(actions.fetchUserManagementByIdFailure(errorMessage));
//         SweetAlert.error({
//             title: 'Error!',
//             text: errorMessage
//         });
//     }
// }

// // dropdown for Master Tenants saga
// function* dropdownTenantsSaga() {
//     try {
//         const response = yield call(adminServices.getAllMasterTenantsDropdown);
//         if (!response) {
//             throw new Error('No response received from API');
//         }
//         // Dispatch success action with the data
//         yield put(actions.dropdownTenantsSuccess(response?.values));

//     } catch (error) {
//         let errorMessage = 'Failed to fetch tenants dropdown';

//         console.error('Tenants Dropdown API Error:', {
//             status: error.response?.status,
//             message: errorMessage,
//             data: error.response?.data,
//             timestamp: new Date().toISOString()
//         });
//         // Dispatch failure action with error
//         yield put(actions.dropdownTenantsFailure(errorMessage));
//     }

// }
// // dropdown for Master Tenants RegionalPractice saga
// function* dropdownUserRegionalPracticeSaga() {
//     try {
//         const response = yield call(adminServices.getUserRegionalPracticeDropdown);
//         if (!response) {
//             throw new Error('No response received from API');
//         }
//         yield put(actions.dropdownUserRegionalPracticeSuccess(response?.values));
//     } catch (error) {
//         let errorMessage = 'Failed to fetch regional practice dropdown';

//         console.error('Regional Practice Dropdown API Error:', {
//             status: error.response?.status,
//             message: errorMessage,
//             data: error.response?.data,
//             timestamp: new Date().toISOString()
//         });
//         yield put(actions.dropdownUserRegionalPracticeFailure(errorMessage));
//     }
// }

// // User Management - Main Practice dropdown saga (distinct from other main practice flows)
// function* dropdownUserMainPracticeSaga() {
//     try {
//         const response = yield call(adminServices.getUserMainPracticeDropdown);
//         if (!response) {
//             throw new Error('No response received from API');
//         }
//         yield put(actions.dropdownUserMainPracticeSuccess(response?.values));
//     } catch (error) {
//         let errorMessage = 'Failed to fetch main practice dropdown for user management';

//         console.error('User Main Practice Dropdown API Error:', {
//             status: error.response?.status,
//             message: errorMessage,
//             data: error.response?.data,
//             timestamp: new Date().toISOString()
//         });
//         yield put(actions.dropdownUserMainPracticeFailure(errorMessage));
//     }
// }

// //------------------User Management - end------------------//

//------------------User Management------------------//
// Worker saga: will be fired on FETCH_USERMANAGEMENT_REQUEST actions
function* fetchUserManagementSaga() {
  try {
    // Call the API - request utility already returns response.data
    const response = yield call(adminServices.getAllUserManagement);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchUserManagementSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch User Management";

    console.error("User Management API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });

    // Dispatch failure action with error
    yield put(actions.fetchUserManagementFailure(errorMessage));
  }
}

// Worker saga for delete user management
function* deleteUserManagementSaga(action) {
  try {
    const { radUserId, modifiedBy } = action.payload;

    // Call the API
    const response = yield call(
      adminServices.deleteUserManagement,
      radUserId,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.deleteUserManagementSuccess(radUserId));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Deleted User Management",
      });
      yield put(actions.fetchUserManagementRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete user management";
      yield put(actions.deleteUserManagementFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete user management";
    yield put(actions.deleteUserManagementFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for activate/deactivate user management
function* activateDeactivateUserManagementSaga(action) {
  try {
    const { userId, status, modifiedBy } = action.payload;

    // Call the API
    const response = yield call(
      adminServices.activateDeactivateUserManagement,
      userId,
      status,
      modifiedBy,
    );

    if (response?.success) {
      yield put(actions.activateDeactivateUserManagementSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `User Management has been ${status ? "activated" : "deactivated"} successfully`,
      });
      yield put(actions.fetchUserManagementRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to update user management status";
      yield put(actions.activateDeactivateUserManagementFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update user management status";
    yield put(actions.activateDeactivateUserManagementFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for save user management (add/update)
function* saveUserManagementSaga(action) {
  try {
    // Call the API
    const response = yield call(
      adminServices.saveUserManagement,
      action.payload,
    );
    if (response?.success) {
      yield put(actions.saveUserManagementSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "User successfully saved",
      });
      yield put(actions.fetchUserManagementRequest());
      if (typeof action.onSuccess === "function") {
        action.onSuccess(response);
      }
    } else {
      const errorMessage = response?.errorMessage || "Failed to save user";
      yield put(actions.saveUserManagementFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save user";
    yield put(actions.saveUserManagementFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for fetch subpractice by ID
function* fetchUserManagementIdSaga(action) {
  try {
    const { userId, userType } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.getUSerManagementById,
      userId,
      userType,
    );
    const values = response?.values || {};

    const payload = {
      selectedUserManagement: values?.userDetails || null,
      userManagementRole: values?.roleDropdown || { dropdownItems: [] },
      userManagementDesignation: values?.designationDropdown || {
        dropdownItems: [],
      },
      tenantDropdown: values?.tenantDropdown || { dropdownItems: [] },
    };

    // Dispatch success action
    yield put(actions.fetchUserManagementByIdSuccess(payload));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch user management details";
    yield put(actions.fetchUserManagementByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for reset MFA user management
export function* resetUserMfaSaga(action) {
  try {
    const { userId } = action.payload;

    const response = yield call(adminServices.resetUserMfa, userId);

    if (response?.success) {
      yield put(actions.resetUserMfaSuccess(response?.values));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "MFA Reset Successfully",
      });
      if (action.meta?.onSuccess) {
        action.meta.onSuccess();
      }
    } else {
      const errorMessage = response?.errorMessage || "Failed to reset MFA";
      yield put(actions.resetUserMfaFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to reset MFA";
    yield put(actions.resetUserMfaFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

//------------------Holiday Planner - start------------------//
function* fetchHolidayPlannerSaga(action) {
  try {
    const year = action.payload?.year;
    const response = yield call(adminServices.getAllHolidayPlanner, year);
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.fetchHolidayPlannerSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch holiday planners";
    console.error("Holiday Planner API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.fetchHolidayPlannerFailure(errorMessage));
  }
}

function* saveHolidayPlannerSaga(action) {
  try {
    // Call the API
    const response = yield call(
      adminServices.saveHolidayPlanner,
      action.payload,
    );
    if (response?.success) {
      yield put(actions.saveHolidayPlannerSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Saved Holiday Planner",
      });
      const year = action.payload?.Year;
      if (year) {
        yield put(actions.fetchHolidayPlannerRequest(year));
      }
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save holiday planner";
      yield put(actions.saveHolidayPlannerFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save holiday planner";
    yield put(actions.saveHolidayPlannerFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* deleteHolidayPlannerSaga(action) {
  try {
    const { HolidayID, modifiedBy, year, isCurrentYear, isFutureYear } =
      action.payload;
    const response = yield call(
      adminServices.deleteHolidayPlanner,
      HolidayID,
      modifiedBy,
      year,
      isCurrentYear,
      isFutureYear,
    );
    if (response?.success) {
      yield put(actions.deleteHolidayPlannerSuccess(HolidayID));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Successfully Deleted Holiday Planner",
      });
      yield put(actions.fetchHolidayPlannerRequest(year));
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete holiday planner";
      yield put(actions.deleteHolidayPlannerFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete holiday planner";
    yield put(actions.deleteHolidayPlannerFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* activateDeactivateHolidayPlannerSaga(action) {
  try {
    const { HolidayID, status, userId, year, isCurrentYear, isFutureYear } =
      action.payload;
    const response = yield call(
      adminServices.activateDeactivateHolidayPlanner,
      HolidayID,
      status,
      userId,
      year,
      isCurrentYear,
      isFutureYear,
    );
    if (response?.success) {
      yield put(actions.activateDeactivateHolidayPlannerSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Holiday Planner has been ${status ? "activated" : "deactivated"} successfully`,
      });
      yield put(actions.fetchHolidayPlannerRequest(year));
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to update holiday planner status";
      yield put(actions.activateDeactivateHolidayPlannerFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update holiday planner status";
    yield put(actions.activateDeactivateHolidayPlannerFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// function* saveHolidayPlannerBulkUpdateSaga(action) {
//     try {
//         // Call the API
//         const response = yield call(adminServices.saveHolidayPlannerBulkUpdate, action.payload.data);

//         if (response && response.success === true) {
//             // Dispatch success action
//             yield put(actions.holidayPlannerBulkUpdateSuccess(response));
//             SweetAlert.success({
//                 title: 'Success!',
//                 text: response?.resultMessage || 'Successfully Saved Holiday Planner Configuration'
//             });
//             yield put(actions.fetchHolidayPlannerRequest(action.payload.year));
//         } else if (response && response.success === false) {
//             const errorMessage = response.errorMessage || 'Failed to save Holiday Planner Configuration';
//             // Dispatch failure action with the specific error message
//             yield put(actions.holidayPlannerBulkUpdateFailure(errorMessage));
//             SweetAlert.error({
//                 title: 'Error!',
//                 text: errorMessage
//             });
//         } else {
//             // Unexpected response format
//             const errorMessage = 'Unexpected response from server';
//             yield put(actions.holidayPlannerBulkUpdateFailure(errorMessage));
//             SweetAlert.error({
//                 title: 'Error!',
//                 text: errorMessage
//             });
//         }
//     } catch (error) {
//         const errorMessage = error.response?.data?.errorMessage || error.message || 'Failed to save Coverage Gap';
//         yield put(actions.holidayPlannerBulkUpdateFailure(errorMessage));
//         SweetAlert.error({
//             title: 'Error!',
//             text: errorMessage
//         });
//     }
// }
//------------------Holiday Planner - end------------------//

//----Holiday Demand Planner-----//

function* saveHolidayPlannerBulkUpdateSaga(action) {
  try {
    const response = yield call(
      adminServices.saveHolidayPlannerBulkUpdate,
      action.payload.data,
    );
    if (response) {
      const itemCode = response?.values?.Item1;
      const message = response?.values?.Item2 || response?.resultMessage;
      if (response.success === true && itemCode === 1) {
        yield put(actions.holidayPlannerBulkUpdateSuccess(response));
        SweetAlert.success({
          title: "Success!",
          text: response?.resultMessage,
        });
        yield put(actions.fetchHolidayPlannerRequest(action.payload.year));
      } else if (response.success === true && itemCode !== 1) {
        yield put(actions.holidayPlannerBulkUpdateFailure(message));
        SweetAlert.warning({
          title: "Warning!",
          text: message,
        });
      } else if (response.success === false) {
        const errorMessage = response.errorMessage;
        yield put(actions.holidayPlannerBulkUpdateFailure(errorMessage));
        SweetAlert.error({
          title: "Error!",
          text: response?.errorMessage,
        });
      } else {
        const errorMessage = "Unexpected response from server";
        yield put(actions.holidayPlannerBulkUpdateFailure(errorMessage));
        SweetAlert.error({
          title: "Error!",
          text: response?.errorMessage,
        });
      }
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save Holiday Planner Configuration";
    yield put(actions.holidayPlannerBulkUpdateFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchHolidayDemandPlannerSaga(action) {
  try {
    const response = yield call(
      adminServices.getAllHolidayDemandPlanner,
      action.payload,
    );

    yield put(actions.fetchHolidayDemandPlannerSuccess(response.values));
  } catch (error) {
    yield put(
      actions.fetchHolidayDemandPlannerFailure(
        "Failed to fetch Holiday Demand Planner data",
      ),
    );
  }
}

function* templateDownloadHolidayDemandPlannerSaga(action) {
  try {
    const response = yield call(
      adminServices.templateDownloadHolidayDemandPlanner,
      action.payload,
    );
    // Use response.values as the base64 string for the file
    yield call(
      downloadBase64File,
      response.values,
      "Holiday_Demand_Planner_Template",
    );
    yield put(
      actions.templateDownloadHolidayDemandPlannerSuccess(response.values),
    );
  } catch (error) {
    yield put(
      actions.templateDownloadHolidayDemandPlannerFailure(
        "Failed to download Holiday Demand Planner template",
      ),
    );
  }
}

// Bulk save Holiday Demand Planner
function* bulkSaveHolidayDemandPlannerSaga(action) {
  try {
    const response = yield call(
      adminServices.bulkSaveHolidayDemandPlanner,
      action.payload,
    );
    if (response?.success) {
      yield put(actions.bulkSaveHolidayDemandPlannerSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Updated Holiday Coverage Plan Details",
      });
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save Holiday Demand Planner data";
      yield put(actions.bulkSaveHolidayDemandPlannerFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save Holiday Demand Planner data";
    yield put(actions.bulkSaveHolidayDemandPlannerFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* fetchHolidayDemandPlannerByRecentYearSaga(action) {
  try {
    const { RecentTrendDate } = action.payload;
    const response = yield call(
      adminServices.fetchHolidayDemandPlannerByRecentYear,
      RecentTrendDate,
    );
    yield put(
      actions.fetchRecentTrendHolidayDemandPlannerSuccess(response?.values),
    );
  } catch (error) {
    yield put(
      actions.fetchRecentTrendHolidayDemandPlannerFailure(
        "Failed to fetch Holiday Demand Planner Recent Year data",
      ),
    );
  }
}

function* fetchHolidayDemandPlannerByPastYearSaga(action) {
  try {
    const { PastTrendDate } = action.payload;
    const response = yield call(
      adminServices.fetchHolidayDemandPlannerByPastYear,
      PastTrendDate,
    );
    yield put(
      actions.fetchPastTrendHolidayDemandPlannerSuccess(response?.values),
    );
  } catch (error) {
    yield put(
      actions.fetchPastTrendHolidayDemandPlannerFailure(
        "Failed to fetch Holiday Demand Planner Past Year data",
      ),
    );
  }
}

function* fetchHolidayDemandPlannerByIdSaga(action) {
  try {
    const { MappingHolidayDateID } = action.payload;
    const response = yield call(
      adminServices.fetchHolidayDemandPlannerById,
      MappingHolidayDateID,
    );
    yield put(actions.fetchHolidayDemandPlannerByIdSuccess(response?.values));
  } catch (error) {
    yield put(
      actions.fetchHolidayDemandPlannerByIdFailure(
        "Failed to fetch Holiday Demand Planner by ID",
      ),
    );
  }
}

export function* uploadSaveHolidayDemandPlannerSaga(action) {
  try {
    const response = yield call(
      adminServices.uploadSaveHolidayDemandPlanner,
      action.payload,
    );
    yield put(actions.uploadSaveHolidayDemandPlannerSuccess(response));
  } catch (error) {
    yield put(
      actions.uploadSaveHolidayDemandPlannerFailure(
        "Failed to upload Holiday Demand Planner",
      ),
    );
  }
}

export function* logFileDownloadHolidayDemandPlannerSaga(action) {
  try {
    const { HolidayCoveragePastTrendFileID } = action.payload;
    const response = yield call(
      adminServices.logFileDownloadHolidayDemandPlanner,
      HolidayCoveragePastTrendFileID,
    );
    yield call(downloadBase64File, response.values, "LastLogFile");
    yield put(
      actions.logFileDownloadHolidayDemandPlannerSuccess(response.values),
    );
  } catch (error) {
    yield put(
      actions.logFileDownloadHolidayDemandPlannerFailure(
        "Failed to download Holiday Demand Planner log file",
      ),
    );
  }
}

export function* dataFileDownloadHolidayDemandPlannerSaga(action) {
  try {
    const { HolidayFileName } = action.payload;
    const response = yield call(
      adminServices.dataFileDownloadHolidayDemandPlanner,
      HolidayFileName,
    );
    yield call(
      downloadBase64File,
      response.values,
      "Holiday_Demand_Planner_Data_File",
    );
    yield put(
      actions.dataFileDownloadHolidayDemandPlannerSuccess(response.values),
    );
  } catch (error) {
    yield put(
      actions.dataFileDownloadHolidayDemandPlannerFailure(
        "Failed to download Holiday Demand Planner data file",
      ),
    );
  }
}
//----Regional Practice-----//

//-----------------Main Practice Saga start from here - Watcher Saga------------------//
function* fetchMainPracticeSaga() {
  try {
    const response = yield call(adminServices.GetAllMainPractice);

    if (response?.success) {
      yield put(actions.fetchMainPracticeDataSuccess(response?.values));

      // Optional success alert (kept commented like your example)
      // SweetAlert.success({
      //     title: 'Success!',
      //     text: response?.resultMessage
      // });
    } else {
      yield put(
        actions.fetchMainPracticeDataFailure(
          "Failed to fetch main practice list",
        ),
      );

      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage,
      });
    }
  } catch (error) {
    console.error("Main Practice API Error:", error);

    yield put(actions.fetchMainPracticeDataFailure(error?.message));

    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch main practice list.",
    });
  }
}

function* fetchGetAllMainPracticeDDListSaga() {
  try {
    const response = yield call(adminServices.GetAllDropdownList);

    if (response?.success) {
      yield put(
        actions.fetchGetAllMainPracticeDropdownListSuccess(response?.values),
      );

      // Optional success alert (kept commented like your example)
      // SweetAlert.success({
      //     title: 'Success!',
      //     text: response?.resultMessage
      // });
    } else {
      yield put(
        actions.fetchMainPracticeDataFailure(
          "Failed to fetch main practice list",
        ),
      );

      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage,
      });
    }
  } catch (error) {
    console.error("Main Practice API Error:", error);

    yield put(
      actions.fetchGetAllMainPracticeDropdownListFailure(error?.message),
    );

    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch main practice list.",
    });
  }
}

function* saveMainPracticeSaga(action) {
  try {
    const response = yield call(adminServices.SaveMainPractice, action.payload);

    if (response?.success) {
      // Success dispatch
      yield put(actions.saveMainPracticeSuccess(response?.values));

      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage,
      });

      // Refresh main practice list after save
      yield put(actions.fetchMainPracticeDataRequest());
    } else {
      // API responded but success = false
      yield put(actions.saveMainPracticeFailure(response?.errorMessage));

      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage,
      });
    }
  } catch (error) {
    console.error("Save Practice API Error:", error);

    yield put(
      actions.saveMainPracticeFailure(
        error?.message || "Failed to save practice",
      ),
    );

    SweetAlert.error({
      title: "Error!",
      text: "Failed to save practice.",
    });
  }
}

function* deleteMainPracticeSaga(action) {
  try {
    const response = yield call(
      adminServices.deleteMainPractice,
      action.payload,
    );

    if (response?.success) {
      // Success dispatch
      yield put(actions.deleteMainPracticeSuccess(response?.values));
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage,
      });

      // Refresh main practice list after save
      yield put(actions.fetchMainPracticeDataRequest());
    } else {
      // API responded but success = false
      yield put(actions.deleteMainPracticeFailure(response?.errorMessage));

      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage,
      });
    }
  } catch (error) {
    console.error("Save Practice API Error:", error);

    yield put(
      actions.saveMainPracticeFailure(
        error?.message || "Failed to save practice",
      ),
    );

    SweetAlert.error({
      title: "Error!",
      text: "Failed to save practice.",
    });
  }
}

function* ActivateDeactivateMainPracticeSaga(action) {
  try {
    const response = yield call(
      adminServices.ActivateMainPrctice,
      action.payload,
    );

    if (response?.success) {
      // Success dispatch
      yield put(
        actions.activatedeactivateMainPracticeSuccess(response?.values),
      );
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage,
      });

      // Refresh main practice list after save
      yield put(actions.fetchMainPracticeDataRequest());
    } else {
      // API responded but success = false
      yield put(
        actions.activatedeactivateMainPracticeFailure(response?.errorMessage),
      );

      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage,
      });
    }
  } catch (error) {
    console.error("Save Practice API Error:", error);

    yield put(
      actions.saveMainPracticeFailure(
        error?.message || "Failed to save practice",
      ),
    );

    SweetAlert.error({
      title: "Error!",
      text: "Failed to save practice.",
    });
  }
}

function* GetMainPracticeDetailByIDSaga(action) {
  try {
    const response = yield call(
      adminServices.GetMainPracticeByID,
      action.payload,
    );

    if (response?.success) {
      // Success dispatch
      yield put(actions.getMainPracticeByIdSuccess(response?.values));
    } else {
      // API responded but success = false
      yield put(actions.getMainPracticeByIdFailure(response?.errorMessage));

      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage,
      });
    }
  } catch (error) {
    console.error("Save Practice API Error:", error);

    yield put(
      actions.saveMainPracticeFailure(
        error?.message || "Failed to save practice",
      ),
    );

    SweetAlert.error({
      title: "Error!",
      text: "Failed to save practice.",
    });
  }
}
//-----------------Main Practice Saga end here - Watcher Saga------------------//

//------------------Regional Practice Worklist - Start------------------//
function* fetchRegionalPracticeWorklistSaga() {
  try {
    const response = yield call(adminServices.getAllRegionalPracticeWorklist);

    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.fetchRegionalPracticeWorklistSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch regional practice worklist";

    console.error("Regional Practice Worklist API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.fetchRegionalPracticeWorklistFailure(errorMessage));
  }
}

function* deleteRegionalPracticeWorklistSaga(action) {
  try {
    const { RegionalPracticeWorklistID, CreatedBy } = action.payload;
    const response = yield call(
      adminServices.deleteRegionalPracticeWorklist,
      RegionalPracticeWorklistID,
      CreatedBy,
    );

    if (response?.success) {
      yield put(
        actions.deleteRegionalPracticeWorklistSuccess(
          RegionalPracticeWorklistID,
        ),
      );
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Deleted Regional Practice Worklist",
      });
      yield put(actions.fetchRegionalPracticeWorklistRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to delete regional practice worklist";
      yield put(actions.deleteRegionalPracticeWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete regional practice worklist";
    yield put(actions.deleteRegionalPracticeWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* saveRegionalPracticeWorklistSaga(action) {
  try {
    const response = yield call(
      adminServices.saveRegionalPracticeWorklist,
      action.payload,
    );
    if (response?.success) {
      yield put(actions.saveRegionalPracticeWorklistSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Successfully Saved Regional Practice Worklist",
      });
      yield put(actions.fetchRegionalPracticeWorklistRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save regional practice worklist";
      yield put(actions.saveRegionalPracticeWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save regional practice worklist";
    yield put(actions.saveRegionalPracticeWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* dropdownRegionalPracticeSaga() {
  try {
    const response = yield call(adminServices.getRegionalPracticeDropdown);
    if (!response) {
      throw new Error("No response received from API");
    }
    yield put(actions.dropdownRegionalPracticeSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch regional practice dropdown";

    console.error("Regional Practice Dropdown API Error:", {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
      timestamp: new Date().toISOString(),
    });
    yield put(actions.dropdownRegionalPracticeFailure(errorMessage));
  }
}

function* fetchRegionalPracticeWorklistByIdSaga(action) {
  try {
    const { RegionalPracticeWorklistID, modifiedBy } = action.payload;
    const response = yield call(
      adminServices.getRegionalPracticeWorklistById,
      RegionalPracticeWorklistID,
      modifiedBy,
    );

    if (response?.success) {
      yield put(
        actions.fetchRegionalPracticeWorklistByIdSuccess(response.values),
      );
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to fetch regional practice worklist details";
      yield put(actions.fetchRegionalPracticeWorklistByIdFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch regional practice worklist details";
    yield put(actions.fetchRegionalPracticeWorklistByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

function* activateDeactivateRegionalPracticeWorklistSaga(action) {
  try {
    const { RegionalPracticeWorklistID, status, userId } = action.payload;
    const response = yield call(
      adminServices.activateDeactivateRegionalPracticeWorklist,
      RegionalPracticeWorklistID,
      status,
      userId,
    );

    if (response?.success) {
      yield put(
        actions.activateDeactivateRegionalPracticeWorklistSuccess(response),
      );
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Regional Practice Worklist has been ${status ? "activated" : "deactivated"} successfully`,
      });
      yield put(actions.fetchRegionalPracticeWorklistRequest());
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to update regional practice worklist status";
      yield put(
        actions.activateDeactivateRegionalPracticeWorklistFailure(errorMessage),
      );
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update regional practice worklist status";
    yield put(
      actions.activateDeactivateRegionalPracticeWorklistFailure(errorMessage),
    );
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}
//------------------Regional Practice Worklist - End------------------//

//---------- Regional Practice - STart ----------//
function* fetchRegionalPracticeSaga() {
  try {
    const response = yield call(adminServices.getAllRegionalPractice);
    if (!response) {
      throw new Error("No response received from API");
    }

    // Dispatch success action with the data
    yield put(actions.fetchRegionalPracticeSuccess(response?.values));
  } catch (error) {
    let errorMessage = "Failed to fetch regional practice";
    // Dispatch failure action with error
    yield put(actions.fetchRegionalPracticeFailure(errorMessage));
  }
}

// delete Regional Practice saga
function* deleteRegionalPracticeSaga(action) {
  try {
    const { RegionalPracticeID, CreatedBy } = action.payload;

    const response = yield call(
      adminServices.deleteRegionalPractice,
      RegionalPracticeID,
      CreatedBy,
    );

    if (response?.success) {
      yield put(actions.deleteRegionalPracticeSuccess(RegionalPracticeID));

      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Regional Practice deleted successfully.",
      });

      yield put(actions.fetchRegionalPracticeRequest());
    } else {
      throw new Error(response?.errorMessage);
    }
  } catch (error) {
    const msg =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to delete regional practice";

    yield put(actions.deleteRegionalPracticeFailure(msg));

    SweetAlert.error({
      title: "Error!",
      text: msg,
    });
  }
}

// save regional practice saga
function* saveRegionalPracticeSaga(action) {
  try {
    // Call the API
    const response = yield call(
      adminServices.saveRegionalPractice,
      action.payload,
    );
    if (response?.success) {
      yield put(actions.saveRegionalPracticeSuccess(response));
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Regional Practice saved successfully.",
      });
      yield put(actions.fetchRegionalPracticeRequest());
    } else {
      yield put(
        actions.saveRegionalPracticeFailure(
          response?.errorMessage || "Failed to save regional practice",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: response?.errorMessage || "Failed to save regional practice.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to save regional practice";
    yield put(actions.saveRegionalPracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for fetch Regional Practice by ID
function* fetchRegionalPracticeByIdSaga(action) {
  try {
    const { regionalPracticeId } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.getRegionalPracticeById,
      regionalPracticeId,
    );
    // Dispatch success action
    yield put(actions.fetchRegionalPracticeByIdSuccess(response.values));
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to fetch regional practice details";
    yield put(actions.fetchRegionalPracticeByIdFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
  }
}

// Worker saga for activate/deactivate Regional Practice
function* activateDeactivateRegionalPracticeSaga(action) {
  try {
    const { regionalPracticeId, status, userId } = action.payload;
    // Call the API
    const response = yield call(
      adminServices.activateDeactivateRegionalPractice,
      regionalPracticeId,
      status,
      userId,
    );
    // Dispatch success action
    yield put(actions.activateDeactivateRegionalPracticeSuccess(response));
    if (response && response.success) {
      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          `Regional Practice has been ${status ? "activated" : "deactivated"} successfully.`,
      });
      yield put(actions.fetchRegionalPracticeRequest());
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to update regional practice status";
      yield put(
        actions.activateDeactivateRegionalPracticeFailure(errorMessage),
      );
      SweetAlert.error({
        title: "Error!",
        text:
          response?.errorMessage ||
          "Failed to update regional practice status.",
      });
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.errorMessage ||
      error.message ||
      "Failed to update regional practice status";
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
    });
    yield put(actions.activateDeactivateRegionalPracticeFailure(errorMessage));
  }
}
//---------- Regional Practice - End ----------//

//------- WorkList Full Save (PracticeMode) -------//
function* saveWorklistFullSaga(action) {
  const { worklistData, practiceMode } = action.payload;
  const isEdit =
    worklistData.WorkListID && String(worklistData.WorkListID) !== "0";
  try {
    const saveResp = yield call(adminServices.saveWorklist, worklistData);
    if (!saveResp?.success) {
      yield put(
        actions.saveWorklistFullFailure(
          saveResp?.errorMessage || "Failed to save worklist",
        ),
      );
      SweetAlert.error({
        title: "Error!",
        text: saveResp?.errorMessage || "Failed to save worklist.",
      });
      return;
    }

    if (isEdit) {
      const workListId = worklistData.WorkListID;

      const modeResp = yield call(
        adminServices.updateWorklistPracticeMode,
        workListId,
        practiceMode,
      );
      if (!modeResp?.success) {
        yield put(
          actions.saveWorklistFullFailure(
            modeResp?.errorMessage || "Failed to update practice mode",
          ),
        );
        SweetAlert.error({
          title: "Error!",
          text: modeResp?.errorMessage || "Failed to update practice mode.",
        });
        return;
      }
    }

    yield put(actions.saveWorklistFullSuccess());
    SweetAlert.success({
      title: "Success!",
      text: "Worklist saved successfully.",
    });
    yield put(actions.fetchWorkListRequest());
  } catch (error) {
    const errorMessage = error.message || "Failed to save worklist";
    yield put(actions.saveWorklistFullFailure(errorMessage));
    SweetAlert.error({ title: "Error!", text: errorMessage });
  }
}
//------- WorkList Full Save End -------//

// Watcher saga: spawns new saga tasks on each request
function* adminSaga() {
  yield takeEvery(types.FETCH_REGIONS_REQUEST, fetchRegionsSaga);
  yield takeEvery(types.DELETE_REGION_REQUEST, deleteRegionSaga);
  yield takeEvery(types.SAVE_REGION_REQUEST, saveRegionSaga);
  yield takeEvery(types.FETCH_REGION_BY_ID_REQUEST, fetchRegionByIdSaga);
  yield takeEvery(types.UPDATE_REGION_REQUEST, updateRegionSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_REGION_REQUEST,
    activateDeactivateRegionSaga,
  );

  yield takeEvery(types.FETCH_NATIONALS_REQUEST, fetchNationalsSaga);
  yield takeEvery(types.DELETE_NATIONAL_REQUEST, deleteNationalSaga);
  yield takeEvery(types.SAVE_NATIONAL_REQUEST, saveNationalSaga);
  yield takeEvery(types.FETCH_NATIONAL_BY_ID_REQUEST, fetchNationalByIdSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_NATIONAL_REQUEST,
    activateDeactivateNationalSaga,
  );

  yield takeEvery(types.FETCH_WORKLIST_REQUEST, fetchWorklistSaga);
  yield takeEvery(types.DELETE_WORKLIST_REQUEST, deleteWorklistSaga);
  yield takeEvery(types.DROPDOWN_WORKLIST_REQUEST, dropdownWorklistSaga);
  yield takeEvery(types.SAVE_WORKLIST_REQUEST, saveWorklistSaga);
  yield takeEvery(types.FETCH_WORKLIST_BY_ID_REQUEST, fetchWorklistByIdSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_WORKLIST_REQUEST,
    activateDeactivateWorklistSaga,
  );
  yield takeEvery(
    types.FETCH_WORKLIST_DEFINITION_REQUEST,
    fetchWorklistDefinitionSaga,
  );
  yield takeEvery(
    types.DROPDOWN_WORKLIST_DEFINITION_TYPE_REQUEST,
    dropdownWorklistDefinitionTypeSaga,
  );
  yield takeEvery(
    types.DROPDOWN_WORKLIST_DEFINITION_FACILITY_REQUEST,
    dropdownWorklistDefinitionFacilitySaga,
  );
  yield takeEvery(
    types.SAVE_WORKLIST_DEFINITION_REQUEST,
    saveWorklistDefinitionSaga,
  );
  yield takeEvery(
    types.DELETE_WORKLIST_DEFINITION_REQUEST,
    deleteWorklistDefinitionSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_WORKLIST_DEFINITION_REQUEST,
    activateDeactivateWorklistDefinitionSaga,
  );
  yield takeEvery(types.FETCH_WORKLIST_GROUP_REQUEST, fetchWorklistGroupSaga);
  yield takeEvery(
    types.DROPDOWN_WORKLIST_GROUP_WORKLIST_REQUEST,
    dropdownWorklistGroupWorklistSaga,
  );
  yield takeEvery(types.SAVE_WORKLIST_GROUP_REQUEST, saveWorklistGroupSaga);
  yield takeEvery(
    types.DELETE_WORKLIST_GROUP_REQUEST,
    deleteWorklistGroupSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_WORKLIST_GROUP_REQUEST,
    activateDeactivateWorklistGroupSaga,
  );
  yield takeEvery(
    types.FETCH_TAG_CATEGORY_CONFIGURATION_REQUEST,
    fetchTagCategoryConfigurationSaga,
  );
  yield takeEvery(
    types.FETCH_TAG_CATEGORY_CONFIGURATION_BYID_REQUEST,
    fetchTagCategoryConfigurationByIdSaga,
  );
  yield takeEvery(
    types.SAVE_TAG_CATEGORY_CONFIGURATION_REQUEST,
    saveTagCategoryConfigurationSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_TAG_CATEGORY_CONFIGURATION_REQUEST,
    activateDeactivateTagCategoryConfigurationSaga,
  );
  yield takeLatest(types.FETCH_MANAGE_TAGS_SHELL_REQUEST, fetchManageTagsShellSaga);
  yield takeLatest(types.FETCH_TAG_ASSIGNMENTS_REQUEST, fetchTagAssignmentsSaga);
  yield takeEvery(types.SAVE_MANAGE_TAGS_BATCH_REQUEST, saveManageTagsBatchSaga);
  yield takeEvery(types.SAVE_TAG_REQUEST, saveTagSaga);
  yield takeEvery(types.DELETE_TAG_REQUEST, deleteTagSaga);
  yield takeEvery(
    types.FETCH_WORKLIST_DEFINITION_CONFIGURATION_REQUEST,
    fetchWorklistDefinitionConfigurationSaga,
  );
  yield takeEvery(
    types.FETCH_WORKLIST_DEFINITION_CONFIGURATION_DROPDOWNS_REQUEST,
    fetchWorklistDefinitionConfigurationDropdownsSaga,
  );
  yield takeEvery(
    types.FETCH_WORKLIST_DEFINITION_CONFIGURATION_BYID_REQUEST,
    fetchWorklistDefinitionConfigurationByIdSaga,
  );
  yield takeEvery(
    types.SAVE_WORKLIST_DEFINITION_CONFIGURATION_REQUEST,
    saveWorklistDefinitionConfigurationSaga,
  );
  yield takeEvery(
    types.DELETE_WORKLIST_DEFINITION_CONFIGURATION_REQUEST,
    deleteWorklistDefinitionConfigurationSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_WORKLIST_DEFINITION_CONFIGURATION_REQUEST,
    activateDeactivateWorklistDefinitionConfigurationSaga,
  );
  //yield takeEvery(types.DROPDOWN_WORKLIST_REGION_REQUEST, dropdownWorklistRegionSaga);
  //yield takeEvery(types.DROPDOWN_WORKLIST_RPCE_REQUEST, dropdownWorklistRPCESaga);
  yield takeEvery(
    types.DROPDOWN_REGIONAL_PRACTICE_WORKLIST_REQUEST,
    dropdownRegionalPracticeWorklistSaga,
  );
  // yield for all master dropdowns (region, rpce, regional practices, worklist category)
  yield takeEvery(
    types.DROPDOWN_ALL_MASTER_WORKLIST_REQUEST,
    dropdownMasterWorklistSaga,
  );
  //yield takeEvery(types.DROPDOWN_REGION_PRACTICE_REQUEST, dropdownAddRegionalPracticeSaga);
  yield takeEvery(
    types.DROPDOWN_MAIN_PRACTICE_REQUEST,
    dropdownMainPracticeSaga,
  );
  //yield takeEvery(types.DROPDOWN_WORKLIST_CATEGORY_REQUEST, dropdownWorklistCategorySaga);

  yield takeEvery(types.FETCH_RPCE_REQUEST, fetchRPCESaga);
  yield takeEvery(types.DELETE_RPCE_REQUEST, deleteRPCESaga);
  yield takeEvery(types.SAVE_RPCE_REQUEST, saveRPCESaga);
  yield takeEvery(types.FETCH_RPCE_BY_ID_REQUEST, fetchRPCEByIdSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_RPCE_REQUEST,
    activateDeactivateRPCESaga,
  );

  yield takeEvery(types.FETCH_PRACTICE_REQUEST, fetchPracticeSaga);
  yield takeEvery(types.FETCH_PRACTICE_BY_ID_REQUEST, fetchpracticeByIdSaga);
  yield takeEvery(types.DROPDOWN_WORKLISTDD_REQUEST, worklistDropdownDD);
  yield takeEvery(types.SAVE_PRACTICE_REQUEST, savePracticeSaga);
  yield takeEvery(types.DELETE_PRACTICE_REQUEST, deletePracticeSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_PRACTICE_REQUEST,
    activateDeactivatePracticeSaga,
  );
  yield takeEvery(
    types.DROPDOWN_REGION_BY_WORKLISTID_REQUEST,
    fetchRegionByWorklistIdSaga,
  );
  yield takeEvery(
    types.DROPDOWN_MASTER_REGION_REQUEST,
    fetchMasterRegionDropdownSaga,
  );
  yield takeEvery(
    types.DROPDOWN_MASTER_NATIONAL_REQUEST,
    fetchMasterNationalDropdownSaga,
  );

  yield takeEvery(types.FETCH_SUBPRACTICES_REQUEST, fetchSubpracticeSaga);
  yield takeEvery(types.DELETE_SUBPRACTICE_REQUEST, deleteSubpracticeSaga);
  yield takeEvery(types.SAVE_SUBPRACTICE_REQUEST, saveSubpracticeSaga);
  yield takeEvery(
    types.FETCH_SUBPRACTICE_BY_ID_REQUEST,
    fetchSubpracticeByIdSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_SUBPRACTICE_REQUEST,
    activateDeactivateSubpracticeSaga,
  );
  yield takeEvery(types.DROPDOWN_SUBPRACTICE_REQUEST, dropdownSubpracticeSaga);

  yield takeEvery(
    types.FETCH_SECTIONWORKLIST_REQUEST,
    fetchSectionWorklistSaga,
  );
  yield takeEvery(
    types.DELETE_SECTIONWORKLIST_REQUEST,
    deleteSectionWorklistSaga,
  );
  yield takeEvery(types.SAVE_SECTIONWORKLIST_REQUEST, saveSectionWorklistSaga);
  yield takeEvery(
    types.FETCH_SECTIONWORKLIST_BY_ID_REQUEST,
    fetchSectionWorklistByIdSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_SECTIONWORKLIST_REQUEST,
    activateDeactivateSectionWorklistSaga,
  );
  yield takeEvery(
    types.DROPDOWN_SECTIONWORKLIST_REQUEST,
    dropdownSectionWorklistSaga,
  );

  yield takeEvery(types.FETCH_USERMANAGEMENT_REQUEST, fetchUserManagementSaga);
  yield takeEvery(
    types.DELETE_USERMANAGEMENT_REQUEST,
    deleteUserManagementSaga,
  );
  yield takeEvery(types.SAVE_USERMANAGEMENT_REQUEST, saveUserManagementSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_USERMANAGEMENT_REQUEST,
    activateDeactivateUserManagementSaga,
  );
  yield takeEvery(
    types.FETCH_USERMANAGEMENT_BY_ID_REQUEST,
    fetchUserManagementIdSaga,
  );
  yield takeLatest(types.RESET_USER_MFA_REQUEST, resetUserMfaSaga);
  // yield takeEvery(types.DROPDOWN_TENANTS_REQUEST, dropdownTenantsSaga);
  // yield takeEvery(types.DROPDOWN_USER_REGIONAL_PRACTICE_REQUEST, dropdownUserRegionalPracticeSaga);
  // yield takeEvery(types.DROPDOWN_USER_MAIN_PRACTICE_REQUEST, dropdownUserMainPracticeSaga);

  yield takeEvery(types.FETCH_HOLIDAY_PLANNER_REQUEST, fetchHolidayPlannerSaga);
  yield takeEvery(types.SAVE_HOLIDAY_PLANNER_REQUEST, saveHolidayPlannerSaga);
  yield takeEvery(
    types.DELETE_HOLIDAY_PLANNER_REQUEST,
    deleteHolidayPlannerSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_HOLIDAY_PLANNER_REQUEST,
    activateDeactivateHolidayPlannerSaga,
  );
  yield takeEvery(
    types.HOLIDAY_PLANNER_BULK_UPDATE_REQUEST,
    saveHolidayPlannerBulkUpdateSaga,
  );

  yield takeEvery(
    types.FETCH_HOLIDAY_DEMANDPLANNER_REQUEST,
    fetchHolidayDemandPlannerSaga,
  );
  yield takeEvery(
    types.Template_DOWNLOAD_HOLIDAY_DEMANDPLANNER_REQUEST,
    templateDownloadHolidayDemandPlannerSaga,
  );
  yield takeEvery(
    types.BULK_SAVE_HOLIDAY_DEMANDPLANNER_REQUEST,
    bulkSaveHolidayDemandPlannerSaga,
  );
  yield takeEvery(
    types.FETCH_RECENT_TREND_HOLIDAY_DEMANDPLANNER_REQUEST,
    fetchHolidayDemandPlannerByRecentYearSaga,
  );
  yield takeEvery(
    types.FETCH_PAST_TREND_HOLIDAY_DEMANDPLANNER_REQUEST,
    fetchHolidayDemandPlannerByPastYearSaga,
  );
  yield takeEvery(
    types.FETCH_HOLIDAY_DEMANDPLANNER_BY_ID_REQUEST,
    fetchHolidayDemandPlannerByIdSaga,
  );
  yield takeEvery(
    types.UPLOAD_SAVE_HOLIDAY_DEMANDPLANNER_REQUEST,
    uploadSaveHolidayDemandPlannerSaga,
  );
  yield takeEvery(
    types.LOGFILE_DOWNLOAD_HOLIDAY_DEMANDPLANNER_REQUEST,
    logFileDownloadHolidayDemandPlannerSaga,
  );
  yield takeEvery(
    types.DATAFILE_DOWNLOAD_HOLIDAY_DEMANDPLANNER_REQUEST,
    dataFileDownloadHolidayDemandPlannerSaga,
  );

  //----------Main Practice Watcher Saga start from here----------//
  yield takeEvery(types.FETCH_MAIN_PRACTICE_REQUEST, fetchMainPracticeSaga);
  yield takeEvery(
    types.FETCH_MAIN_PRACTICE_DROPDOWNLIST_REQUEST,
    fetchGetAllMainPracticeDDListSaga,
  );
  yield takeEvery(types.SAVE_MAIN_PRACTICE_REQUEST, saveMainPracticeSaga);
  yield takeEvery(types.DELETE_MAIN_PRACTICE_REQUEST, deleteMainPracticeSaga);
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_MAIN_PRACTICE_REQUEST,
    ActivateDeactivateMainPracticeSaga,
  );
  yield takeEvery(
    types.GET_MAIN_PRACTICE_BY_ID_REQUEST,
    GetMainPracticeDetailByIDSaga,
  );
  //----------Main Practice Watcher Saga end here----------//

  //---------- Regional Practice Worklist - Start ----------//
  yield takeEvery(
    types.FETCH_REGIONAL_PRACTICE_WORKLIST_REQUEST,
    fetchRegionalPracticeWorklistSaga,
  );
  yield takeEvery(
    types.DELETE_REGIONAL_PRACTICE_WORKLIST_REQUEST,
    deleteRegionalPracticeWorklistSaga,
  );
  yield takeEvery(
    types.SAVE_REGIONAL_PRACTICE_WORKLIST_REQUEST,
    saveRegionalPracticeWorklistSaga,
  );
  yield takeEvery(
    types.FETCH_REGIONAL_PRACTICE_WORKLIST_BY_ID_REQUEST,
    fetchRegionalPracticeWorklistByIdSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_REGIONAL_PRACTICE_WORKLIST_REQUEST,
    activateDeactivateRegionalPracticeWorklistSaga,
  );
  yield takeEvery(
    types.DROPDOWN_REGIONAL_PRACTICE_REQUEST,
    dropdownRegionalPracticeSaga,
  );
  //---------- Regional Practice Worklist - End ----------//

  //---------- Regional Practice - STart ----------//
  yield takeEvery(
    types.FETCH_REGIONAL_PRACTICE_REQUEST,
    fetchRegionalPracticeSaga,
  );
  yield takeEvery(
    types.DELETE_REGIONAL_PRACTICE_REQUEST,
    deleteRegionalPracticeSaga,
  );
  yield takeEvery(
    types.SAVE_REGIONAL_PRACTICE_REQUEST,
    saveRegionalPracticeSaga,
  );
  yield takeEvery(
    types.FETCH_REGIONAL_PRACTICE_BY_ID_REQUEST,
    fetchRegionalPracticeByIdSaga,
  );
  yield takeEvery(
    types.ACTIVATE_DEACTIVATE_REGIONAL_PRACTICE_REQUEST,
    activateDeactivateRegionalPracticeSaga,
  );
  //---------- Regional Practice - End ----------//

  yield takeEvery(types.SAVE_WORKLIST_FULL_REQUEST, saveWorklistFullSaga);
}

export default adminSaga;
